// ============================================
// MEDFLOW - Dashboard Layout
// ============================================

import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  UserCog,
  Stethoscope,
  DoorOpen,
  Wallet,
  Settings,
  Menu,
  X,
  Bot,
  LogOut,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

const CLINIC_NAME = import.meta.env.VITE_CLINIC_NAME || 'Medflow';

const mainNav = [
  { name: 'Calendario', href: '/calendar', icon: Calendar },
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Pacientes', href: '/patients', icon: Users },
  { name: 'Profesionales', href: '/professionals', icon: UserCog },
  { name: 'Agentes IA', href: '/agents', icon: Bot },
];

const secondaryNav = [
  { name: 'Servicios', href: '/services', icon: Stethoscope },
  { name: 'Boxes', href: '/boxes', icon: DoorOpen },
  { name: 'Finanzas', href: '/finances', icon: Wallet },
  { name: 'Configuración', href: '/settings', icon: Settings },
];

// Map routes to readable page titles
const pageTitles: Record<string, string> = {
  '/calendar': 'Calendario',
  '/dashboard': 'Dashboard',
  '/patients': 'Pacientes',
  '/professionals': 'Profesionales',
  '/agents': 'Agentes IA',
  '/services': 'Servicios',
  '/boxes': 'Boxes',
  '/finances': 'Finanzas',
  '/settings': 'Configuración',
};

function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
        <svg className="w-4.5 h-4.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M12 4v16m-8-8h16" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <span className="text-lg font-bold tracking-tight text-gray-900">Medflow</span>
    </div>
  );
}

function NavItem({ item, onClick }: { item: typeof mainNav[0]; onClick?: () => void }) {
  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={({ isActive }) => cn(
        'group flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150',
        isActive
          ? 'bg-blue-50 text-blue-700'
          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
      )}
    >
      {({ isActive }) => (
        <>
          <item.icon className={cn(
            'w-[18px] h-[18px] transition-colors',
            isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
          )} />
          <span>{item.name}</span>
        </>
      )}
    </NavLink>
  );
}

function UserSection({ user, clinicName, onLogout }: { user: { name: string } | null; clinicName: string; onLogout: () => void }) {
  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="p-3 border-t border-gray-100">
      <div className="flex items-center gap-3 px-2 py-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
          <span className="text-[11px] font-semibold text-white">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{user?.name ?? 'Usuario'}</p>
          <p className="text-[11px] text-gray-400 truncate">{clinicName}</p>
        </div>
        <button
          onClick={onLogout}
          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Cerrar sesión"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SidebarContent({ onNavigate, user, clinicName, onLogout }: {
  onNavigate?: () => void;
  user: { name: string } | null;
  clinicName: string;
  onLogout: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex h-14 items-center px-5">
        <Logo />
      </div>

      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {mainNav.map((item) => (
          <NavItem key={item.name} item={item} onClick={onNavigate} />
        ))}

        <div className="my-3">
          <div className="h-px bg-gray-100 mx-2" />
        </div>

        {secondaryNav.map((item) => (
          <NavItem key={item.name} item={item} onClick={onNavigate} />
        ))}
      </nav>

      <UserSection user={user} clinicName={clinicName} onLogout={onLogout} />
    </div>
  );
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentPageTitle = pageTitles[location.pathname] || '';

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-[264px] bg-white shadow-elevated">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-3.5 right-3 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent
              onNavigate={() => setSidebarOpen(false)}
              user={user}
              clinicName={CLINIC_NAME}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-[248px] lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-100">
          <SidebarContent
            user={user}
            clinicName={CLINIC_NAME}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pl-[248px]">
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <div className="flex h-14 items-center gap-4 px-4 sm:px-6 lg:px-8">
            <button
              className="lg:hidden p-1.5 -ml-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Breadcrumb / Page title */}
            <div className="hidden sm:flex items-center gap-1.5 text-sm">
              <span className="text-gray-400">{CLINIC_NAME}</span>
              {currentPageTitle && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                  <span className="font-medium text-gray-700">{currentPageTitle}</span>
                </>
              )}
            </div>

            <div className="flex-1" />

            {/* Mobile: show page title centered */}
            <span className="sm:hidden text-sm font-medium text-gray-700">{currentPageTitle}</span>
            <div className="sm:hidden flex-1" />

            <button
              onClick={handleLogout}
              className="lg:hidden p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
